import {
  getDay,
  getYear,
  getMonth,
  getDate,
  endOfMonth,
  getHours,
  getMinutes,
  getSeconds,
  addYears,
  addMonths,
  addDays,
  setYear,
  setMonth,
  setDate,
  setHours,
  setMinutes,
  setSeconds,
  isAfter,
  isValid,
  getWeek,
  startOfWeek,
  format as formatDate,
  parse as parseDate,
} from 'date-fns';
import { enAU } from 'date-fns/locale';
import type { GenerateConfig } from 'rc-picker/lib/generate';

const localeParse = (format: string) => {
  return format
    .replace(/Y/gu, 'y')
    .replace(/D/gu, 'd')
    .replace(/gggg/u, 'yyyy')
    .replace(/g/gu, 'G')
    .replace(/([Ww])o/gu, 'wo');
};

/**
 * Custom date-fns config generation for Antd Date picker's default library.
 * This is used to reduce the production bundle size of date-fns, since the default date-fns generate config for
 * Antd Date picker will bundle all locales, and significant increases the production bundle size. The custom generator
 * will only include en-AU locale and can be extended in the future.
 * The custom generator is specifically using enAU locale without checking the passed in locale.
 * @see https://github.com/react-component/picker/blob/master/src/generate/dateFns.ts
 * @see https://github.com/react-component/picker/issues/125
 */
const dateFnsGenerateConfig: GenerateConfig<Date> = {
  // get
  getNow: () => new Date(),
  getFixedDate: (string) => new Date(string),
  getEndDate: (date) => endOfMonth(date),
  getWeekDay: (date) => getDay(date),
  getYear: (date) => getYear(date),
  getMonth: (date) => getMonth(date),
  getDate: (date) => getDate(date),
  getHour: (date) => getHours(date),
  getMinute: (date) => getMinutes(date),
  getSecond: (date) => getSeconds(date),

  // set
  addYear: (date, diff) => addYears(date, diff),
  addMonth: (date, diff) => addMonths(date, diff),
  addDate: (date, diff) => addDays(date, diff),
  setYear: (date, year) => setYear(date, year),
  setMonth: (date, month) => setMonth(date, month),
  setDate: (date, num) => setDate(date, num),
  setHour: (date, hour) => setHours(date, hour),
  setMinute: (date, minute) => setMinutes(date, minute),
  setSecond: (date, second) => setSeconds(date, second),

  // Compare
  isAfter: (date1, date2) => isAfter(date1, date2),
  isValidate: (date) => isValid(date),

  locale: {
    getWeekFirstDay: () => enAU.options?.weekStartsOn ?? 0,
    getWeekFirstDate: (_locale, date) => {
      return startOfWeek(date, { locale: enAU });
    },
    getWeek: (_locale, date) => {
      return getWeek(date, { locale: enAU });
    },
    getShortWeekDays: () =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      Array.from({ length: 7 }).map((_, i) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        enAU.localize?.day(i, { width: 'short' })
      ),
    getShortMonths: () =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      Array.from({ length: 12 }).map((_, i) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        enAU.localize?.month(i, { width: 'abbreviated' })
      ),
    format: (_locale, date, format) => {
      if (!isValid(date)) {
        return '';
      }
      return formatDate(date, localeParse(format), {
        locale: enAU,
      });
    },
    parse: (_locale, text, formats) => {
      // eslint-disable-next-line functional/no-loop-statements, functional/no-let
      for (let i = 0; i < formats.length; i += 1) {
        const format = localeParse(formats[i] ?? '');
        const formatText = text;
        const date = parseDate(formatText, format, new Date(), {
          locale: enAU,
        });
        /**
         * Since the date-fns do not have a strict parse, e.g. parse 12:1 as valid for format HH:mm.
         * We format the parsed date back to string and compare it to original text to make sure
         * the parse is strict.
         * @see https://github.com/date-fns/date-fns/issues/1569
         */
        if (isValid(date) && formatDate(date, format) === text) {
          return date;
        }
      }
      return null;
    },
  },
};

export default dateFnsGenerateConfig;
